/**
 * Statistics summary card for the game-over screen.
 * Shows rounds played, outcome reason, per-player stone counts.
 */

import type {
  PlayerId,
  StoneFlickingState,
} from "@cloudnest/augmented-stone-flicking-engine";
import { countRemaining } from "@cloudnest/stone-flicking-core-engine/utils";

interface GameOverStatsProps {
  readonly gameState: StoneFlickingState;
  readonly myPlayerId: PlayerId | null;
}

const PLAYER_COLORS: ReadonlyArray<string> = [
  "var(--cn-asf-stone-p1)",
  "var(--cn-asf-stone-p2)",
  "var(--cn-asf-stone-p3)",
  "var(--cn-asf-stone-p4)",
];

export function GameOverStats({ gameState, myPlayerId }: GameOverStatsProps) {
  const participants = gameState.coreState.participants;

  const stats = participants.map((p, idx) => ({
    id: p.id,
    label: p.id,
    color: PLAYER_COLORS[idx % PLAYER_COLORS.length] ?? "var(--cn-asf-text)",
    remaining: countRemaining(gameState.coreState.entities, p.id),
    isMe: p.id === myPlayerId,
  }));

  const maxCount = Math.max(...stats.map((s) => s.remaining), 1);

  return (
    <aside
      style={{
        backgroundColor: "rgba(255,255,255,0.12)",
        backdropFilter: "blur(8px)",
        borderRadius: "12px",
        padding: "16px",
        minWidth: "200px",
        maxWidth: "280px",
      }}
      aria-label="게임 통계"
    >
      {/* Round count */}
      <div
        style={{
          fontSize: "12px",
          color: "rgba(255,255,255,0.6)",
          marginBottom: "12px",
          textAlign: "center",
        }}
      >
        라운드 {gameState.round}
      </div>

      {/* Per-player bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {stats.map((s) => (
          <div
            key={s.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                fontWeight: s.isMe ? 700 : 400,
                color: "#fff",
                width: "24px",
                textAlign: "right",
              }}
            >
              {s.label}
            </span>
            <div
              style={{
                flex: 1,
                height: "8px",
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${(s.remaining / maxCount) * 100}%`,
                  height: "100%",
                  backgroundColor: s.color,
                  borderRadius: "4px",
                  transition: "width 0.5s ease-out",
                }}
              />
            </div>
            <span
              style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.8)",
                width: "20px",
              }}
            >
              {s.remaining}
            </span>
          </div>
        ))}
      </div>
    </aside>
  );
}
